<script setup lang="ts">
import visitingJapanTopLeftVideo from '@/assets/animations/visiting-japan-top-left.mp4';
import visitingJapanBottomRightVideo from '@/assets/animations/visiting-japan-bottom-right.mp4';
import visitingJapanTopRightImage from '@/assets/images/visiting-japan-top-right.jpg';
import visitingJapanBottomLeftImage from '@/assets/images/visiting-japan-bottom-left.jpg';
import visitingJapanCenterImage from '@/assets/images/visiting-japan-center.jpg';
import visitingJapanHeader from '@/assets/images/visiting-japan-header.png';

const { isMobile } = useWindowResize();

const prefersReducedMotion = ref(false);

onMounted(() => {
	prefersReducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
});
</script>

<template>
	<section class="visiting-japan-hero">
		<template v-if="!isMobile">
			<div
				class="media-grid"
				aria-hidden="true"
			>
				<div class="tile tile--top-left">
					<video
						v-if="!prefersReducedMotion"
						autoplay
						loop
						muted
						playsinline
						preload="metadata"
						:poster="visitingJapanCenterImage"
					>
						<source
							:src="visitingJapanTopLeftVideo"
							type="video/mp4"
						>
					</video>
				</div>
				<div class="tile tile--center">
					<img
						:src="visitingJapanCenterImage"
						alt=""
					>
				</div>
				<div class="tile tile--top-right">
					<img
						:src="visitingJapanTopRightImage"
						alt=""
					>
				</div>
				<div class="tile tile--bottom-left">
					<img
						:src="visitingJapanBottomLeftImage"
						alt=""
					>
				</div>
				<div class="tile tile--bottom-right">
					<video
						v-if="!prefersReducedMotion"
						autoplay
						loop
						muted
						playsinline
						preload="metadata"
						:poster="visitingJapanCenterImage"
					>
						<source
							:src="visitingJapanBottomRightVideo"
							type="video/mp4"
						>
					</video>
				</div>
			</div>

			<div class="form-overlay">
				<div class="form-wrapper">
					<div class="header-badge">
						<img
							:src="visitingJapanHeader"
							alt="Visiting Japan"
						>
					</div>
					<div class="form-card">
						<slot />
					</div>
				</div>
			</div>
		</template>

		<template v-else>
			<div
				class="mobile-bg"
				:style="{ backgroundImage: `url(${visitingJapanCenterImage})` }"
			>
				<div class="form-wrapper">
					<div class="header-badge">
						<img
							:src="visitingJapanHeader"
							alt="Visiting Japan"
						>
					</div>
					<div class="form-card">
						<slot />
					</div>
				</div>
			</div>
		</template>
	</section>
</template>

<style scoped lang="scss">
.visiting-japan-hero {
	position: relative;
	height: rem(600);
	box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);
	backdrop-filter: blur(25px);
	background: colors-get(base, black);
	overflow: hidden;

	@include media-query-max(mobile) {
		min-height: auto;
	}

	.media-grid {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 50%;
		transform: translateX(-50%);
		width: 100%;
		min-width: rem(1200);
		display: grid;
		grid-template-columns: 1fr 1.3fr 1fr;
		grid-template-rows: 1fr 1fr;
		grid-template-areas:
			'top-left center top-right'
			'bottom-left center bottom-right';
		gap: spacings-get(4);
		background: colors-get(base, white);
	}

	.tile {
		position: relative;
		overflow: hidden;

		img,
		video {
			position: absolute;
			inset: 0;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		.tile-fallback {
			opacity: 0.75;
		}

		video {
			opacity: 0.9;
		}
	}

	.tile--top-left {
		grid-area: top-left;
	}

	.tile--center {
		grid-area: center;
	}

	.tile--top-right {
		grid-area: top-right;
	}

	.tile--bottom-left {
		grid-area: bottom-left;
	}

	.tile--bottom-right {
		grid-area: bottom-right;
	}

	.form-overlay {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: spacings-get(8) spacings-get(4);
	}

	.form-wrapper {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: rem(30);
		width: 100%;
		max-width: rem(420);
		position: relative;
	}

	.header-badge {
		max-width: rem(360);
		margin: 0 spacings-get(2);

		img {
			width: 100%;
			height: auto;
		}
	}

	.form-card {
		position: relative;
		width: 100%;
		background: #faa3a3;
		padding: spacings-get(8) spacings-get(4) spacings-get(8) spacings-get(8);
		border-radius: rem(20);
		max-height: calc(100% - #{spacings-get(8) * 2});
		overflow: visible;
		z-index: 1;

		&::before {
			content: '';
			position: absolute;
			top: rem(-8);
			left: rem(8);
			right: rem(-8);
			bottom: rem(8);
			background: linear-gradient(100deg, #ee4146 0.85%, #e7316e 74.83%), #ee4146;
			border-radius: rem(20);
			z-index: -1;
		}
	}

	.mobile-bg {
		height: 100%;
		background-position: center;
		background-size: cover;
		background-repeat: no-repeat;
		padding: spacings-get(8) spacings-get(4);

		.form-wrapper {
			max-width: rem(420);
		}

		.form-card {
			max-height: none;
			overflow: visible;
		}
	}
}
</style>
